import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW } from 'constants/experience'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import { useAppSelector } from 'hooks/reduxHooks'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import focusStyle from 'styles/utils/focusStyle'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { useInView } from 'react-intersection-observer'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import { connect } from 'react-redux'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import PriceRowLuxPlusPrice from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'

const Tile = styled(Clickable)`
  display: grid;
  grid-template-columns: ${rem(96)} 1fr;
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.XS};
  overflow: hidden;
  min-height: ${rem(92)};
  box-shadow: none;
  transition: outline 0.2s, box-shadow 0.2s;

  ${mediaQueryUp.tablet} {
    min-height: ${rem(96)};
  }

  &.map-view {
    grid-template-columns: ${rem(180)} 1fr;
    min-height: ${rem(180)};

    ${mediaQueryUp.tablet} {
      min-height: ${rem(180)};
    }
  }

  ${mediaHoverable} {
    &:hover {
      box-shadow: ${props => props.theme.shadow.bottom.small};
    }
  }

  &:focus-visible {
    ${focusStyle}
  }
`

const Content = styled(VerticalSpacer)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(12)} ${rem(16)};

  &.map-view {
    padding: ${rem(20)} ${rem(16)};
  }
`

interface Props {
  experience: App.ExperienceOffer
  position?: number
  luxPlusEnabled: boolean
  isMapView?: boolean
}

function ExperienceRecentlyViewedTile(props: Props) {
  const { experience, position, luxPlusEnabled, isMapView = false } = props
  const intl = useIntl()
  const fromCaption = intl.formatMessage({
    id: '1HqkAJtKjpKq56bGHEDj',
    defaultMessage: 'From',
    description: 'Price caption prefix on experience recently viewed tile',
  })
  const totalSaleUnitLabel = intl.formatMessage({
    id: 'wxF52Xd7WvOgjcZh_zFJ',
    defaultMessage: 'total',
    description: 'Default sale unit label when no ticket unit label on experience recently viewed tile',
  })
  const mainImage = experience.images[0]

  const tripId = useAppSelector(getImmersiveTripId)
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  const onClick = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experience,
        position,
        key: EventDataKey.ProductClick,
      })
    }
  }, [experience, dispatchOfferListEvent, position])

  useEffect(() => {
    if (inView && imageLoaded && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: experience,
        position,
        key: EventDataKey.ProductImpression,
      })
    }
  }, [inView, imageLoaded, position, dispatchOfferListEvent, experience])

  const handleOnLoad = useCallback(() => {
    setImageLoaded(true)
  }, [])

  const linkParams = useMemo(() => {
    const queryParams = (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) ?
        new URLSearchParams({ tripId }) :
      undefined
    const to = getExperienceOfferPageURL(experience, queryParams)
    return {
      to,
      ...(config.OPEN_NEW_TAB_OFFER_CLICK || isMapView ? { target: '_blank' } : {}),
    }
  }, [tripId, experience, isMapView])

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const isLuxPlusOffer = luxPlusEnabled && experience.pricing.memberPrice > 0

  return <Tile {...linkParams} onClick={onClick} ref={inViewRef} className={clsx({ 'map-view': isMapView })}>
    {mainImage && <Image
      image={mainImage}
      height={isMapView ? 180 : 96}
      width={isMapView ? 180 : 96}
      fit="center"
      onLoad={handleOnLoad}
    />}
    <Content gap={8} className={clsx({ 'map-view': isMapView })}>
      <BodyText variant="small" weight="bold" lineClamp={2}>{experience.name}</BodyText>
      <VerticalSpacer gap={2}>
        {experience.discount >= EXPERIENCE_MINIMUM_DISCOUNT_PERCENTAGE_TO_SHOW && <PriceRowValueDiscount
          type="estimate"
          discountPercentage={experience.discount}
        />}
        {!isLuxPlusOffer && <PriceRowPriceWithCaption
          size="S"
          caption={fromCaption}
          price={experience.price}
          saleUnit={experience.ticketUnitLabel}
        />}
        {!!isLuxPlusOffer && <PriceRowLuxPlusPrice
          caption={fromCaption}
          logoPlacement="trailing"
          size="L"
          memberPrice={experience.pricing.memberPrice}
          saleUnit={experience.ticketUnitLabel ? experience.ticketUnitLabel : totalSaleUnitLabel}
        />}
        <PriceRowAgentHubCommission
          offerId={experience.id}
          productType={productType}
          offerLocations={[experience.location.name]}
          offer={experience}
        />
      </VerticalSpacer>
    </Content>
  </Tile>
}

const mapStateToProps = (state: App.State) => {
  return {
    luxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperienceRecentlyViewedTile)
